<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { onClickOutside } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { filterSpaceKbSchemaProperties } from '../composables/useImmutableSchemaKnowledgebase';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Tabs from 'dashboard/components/ui/Tabs/Tabs.vue';
import TemplateFieldControl from './TemplateFieldControl.vue';
import TemplateCardPreview from './TemplateCardPreview.vue';
import TemplateListPreview from './TemplateListPreview.vue';

const props = defineProps({
  knowledgebaseId: {
    type: [String, Number],
    required: true,
  },
});

const emit = defineEmits(['close', 'saved']);

const AVAILABLE_LANGUAGES = [
  { name: 'English (en)', code: 'en' },
  { name: 'العربية (ar)', code: 'ar' },
  { name: 'Nederlands (nl)', code: 'nl' },
  { name: 'Français (fr)', code: 'fr' },
  { name: 'Deutsch (de)', code: 'de' },
  { name: 'हिन्दी (hi)', code: 'hi' },
  { name: 'Italiano (it)', code: 'it' },
  { name: '日本語 (ja)', code: 'ja' },
  { name: '한국어 (ko)', code: 'ko' },
  { name: 'Português (pt)', code: 'pt' },
  { name: 'русский (ru)', code: 'ru' },
  { name: '中文 (zh)', code: 'zh' },
  { name: 'Español (es)', code: 'es' },
  { name: 'മലയാളം (ml)', code: 'ml' },
  { name: 'Català (ca)', code: 'ca' },
  { name: 'ελληνικά (el)', code: 'el' },
  { name: 'Português Brasileiro (pt-BR)', code: 'pt_BR' },
  { name: 'Română (ro)', code: 'ro' },
  { name: 'தமிழ் (ta)', code: 'ta' },
  { name: 'فارسی (fa)', code: 'fa' },
  { name: '中文 (台湾) (zh-TW)', code: 'zh_TW' },
  { name: 'Tiếng Việt (vi)', code: 'vi' },
  { name: 'dansk (da)', code: 'da' },
  { name: 'Türkçe (tr)', code: 'tr' },
  { name: 'čeština (cs)', code: 'cs' },
  { name: 'suomi (fi)', code: 'fi' },
  { name: 'Bahasa Indonesia (id)', code: 'id' },
  { name: 'Svenska (sv)', code: 'sv' },
  { name: 'magyar (hu)', code: 'hu' },
  { name: 'norsk (no)', code: 'no' },
  { name: '中文 (zh-CN)', code: 'zh_CN' },
  { name: 'polski (pl)', code: 'pl' },
  { name: 'slovenčina (sk)', code: 'sk' },
  { name: 'українська (uk)', code: 'uk' },
  { name: 'ภาษาไทย (th)', code: 'th' },
  { name: 'latviešu (lv)', code: 'lv' },
  { name: 'íslenska (is)', code: 'is' },
  { name: 'עִברִית (he)', code: 'he' },
  { name: 'lietuvių (lt)', code: 'lt' },
  { name: 'Српски (sr)', code: 'sr' },
];

const { t } = useI18n();
const store = useStore();

const isSaving = ref(false);
const editingTemplate = ref(null); // null = overview, 'card' or 'list' = editing that template
const selectedLocale = ref('default'); // 'default' or locale code like 'en', 'nl', 'fr'
const localeConfigs = ref({}); // Stores configs per locale: { 'default': { card: {...}, list: {...} }, 'fr': { card: {...} } }
const showLanguageDropdown = ref(false);
const languageDropdownRef = ref(null);
onClickOutside(languageDropdownRef, () => {
  showLanguageDropdown.value = false;
});

const activeLocales = ref(['default']); // locale codes that have been added

const activeLocaleTabs = computed(() =>
  activeLocales.value.map(code => {
    if (code === 'default') return { code: 'default', label: 'Default' };
    const lang = AVAILABLE_LANGUAGES.find(l => l.code === code);
    return { code, label: lang?.name || code };
  })
);

const activeLocaleTabIndex = computed(() => {
  const idx = activeLocales.value.indexOf(selectedLocale.value);
  return idx >= 0 ? idx : 0;
});

const handleLocaleTabChanged = index => {
  switchLocale(activeLocales.value[index]);
};

const addableLanguageMenuItems = computed(() =>
  AVAILABLE_LANGUAGES.filter(l => !activeLocales.value.includes(l.code)).map(
    l => ({ action: 'add', value: l.code, label: l.name })
  )
);

const addLanguage = ({ value: code }) => {
  if (!activeLocales.value.includes(code)) {
    activeLocales.value.push(code);
  }
  showLanguageDropdown.value = false;
  switchLocale(code);
};

const removeLanguage = code => {
  if (code === 'default') return;
  const idx = activeLocales.value.indexOf(code);
  if (idx === -1) return;
  activeLocales.value.splice(idx, 1);
  delete localeConfigs.value[code];
  if (selectedLocale.value === code) {
    switchLocale('default');
  }
};

// Template types available
const templateTypes = computed(() => [
  {
    type: 'card',
    name: t('LIBRARIES.TEMPLATE_CONFIG.CARD.NAME'),
    icon: 'i-lucide-credit-card',
    description: t('LIBRARIES.TEMPLATE_CONFIG.CARD.DESCRIPTION'),
  },
  {
    type: 'list',
    name: t('LIBRARIES.TEMPLATE_CONFIG.LIST.NAME'),
    icon: 'i-lucide-list',
    description: t('LIBRARIES.TEMPLATE_CONFIG.LIST.DESCRIPTION'),
  },
]);

// Available field types that can be added for card templates
const availableCardFieldTypes = computed(() => [
  {
    id: 'title',
    label: t('LIBRARIES.TEMPLATE_CONFIG.FIELDS.TITLE'),
    icon: 'i-lucide-type',
  },
  {
    id: 'description',
    label: t('LIBRARIES.TEMPLATE_CONFIG.FIELDS.DESCRIPTION'),
    icon: 'i-lucide-file-text',
  },
  {
    id: 'media_url',
    label: t('LIBRARIES.TEMPLATE_CONFIG.FIELDS.IMAGE'),
    icon: 'i-lucide-image',
  },
  {
    id: 'uri',
    label: t('LIBRARIES.TEMPLATE_CONFIG.FIELDS.ACTION_URL'),
    icon: 'i-lucide-link',
  },
  {
    id: 'label',
    label: t('LIBRARIES.TEMPLATE_CONFIG.FIELDS.BUTTON_LABEL'),
    icon: 'i-lucide-square-mouse-pointer',
  },
]);

// Available field types for list templates (variables only)
const availableListFieldTypes = computed(() => [
  {
    id: 'title',
    label: t('LIBRARIES.TEMPLATE_CONFIG.FIELDS.TITLE'),
    icon: 'i-lucide-type',
  },
  {
    id: 'description',
    label: t('LIBRARIES.TEMPLATE_CONFIG.FIELDS.DESCRIPTION'),
    icon: 'i-lucide-file-text',
  },
  {
    id: 'uri',
    label: t('LIBRARIES.TEMPLATE_CONFIG.FIELDS.ACTION_URL'),
    icon: 'i-lucide-link',
  },
]);

// Separate configs for each template type
const cardConfig = ref({});
const listConfig = ref({});

const knowledgebase = computed(() =>
  store.getters['knowledgebase/getKnowledgebase'](props.knowledgebaseId)
);

const schema = computed(() => store.getters['knowledgebase/getActiveSchema']);

// Get current config being edited
const currentConfig = computed(() => {
  if (editingTemplate.value === 'list') return listConfig.value;
  if (editingTemplate.value === 'card') return cardConfig.value;
  return {};
});

// Get available field types based on current template type
const availableFieldTypes = computed(() => {
  return editingTemplate.value === 'list'
    ? availableListFieldTypes.value
    : availableCardFieldTypes.value;
});

const availableFields = computed(() => {
  const properties = filterSpaceKbSchemaProperties(
    schema.value?.properties,
    knowledgebase.value
  );
  if (!properties) return [];
  return Object.keys(properties).map(key => ({
    value: key,
    label: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
  }));
});

// Initialize default fields for a template type
const initializeDefaultFields = type => {
  if (type === 'card') {
    // Only initialize if card config is empty
    if (Object.keys(cardConfig.value).length === 0) {
      cardConfig.value = {
        title: {
          type: 'prompt',
          field: '',
          prompt: 'Generate an appropriate title',
        },
        description: {
          type: 'prompt',
          field: '',
          prompt: 'Create a relevant description',
        },
        uri: {
          type: 'property',
          field:
            availableFields.value.length > 0
              ? availableFields.value[0].value
              : '',
          prompt: '',
        },
      };
    }
  } else if (type === 'list') {
    // Only initialize if list config is empty
    if (Object.keys(listConfig.value).length === 0) {
      listConfig.value = {
        title: {
          type: 'property',
          field:
            availableFields.value.length > 0
              ? availableFields.value[0].value
              : '',
          prompt: '',
        },
        description: {
          type: 'property',
          field:
            availableFields.value.length > 1
              ? availableFields.value[1].value
              : '',
          prompt: '',
        },
        uri: {
          type: 'property',
          field:
            availableFields.value.length > 2
              ? availableFields.value[2].value
              : '',
          prompt: '',
        },
      };
    }
  }
};

// Start editing a template
const startEditingTemplate = type => {
  initializeDefaultFields(type);
  editingTemplate.value = type;
};

// Go back to overview
const backToOverview = () => {
  storeCurrentLocaleConfig();
  editingTemplate.value = null;
};

// Store current working cardConfig/listConfig into the locale map
const storeCurrentLocaleConfig = () => {
  const locale = selectedLocale.value;
  if (!localeConfigs.value[locale]) localeConfigs.value[locale] = {};
  localeConfigs.value[locale].card = JSON.parse(
    JSON.stringify(cardConfig.value)
  );
  localeConfigs.value[locale].list = JSON.parse(
    JSON.stringify(listConfig.value)
  );
};

// Switch to a different locale tab
const switchLocale = locale => {
  if (locale === selectedLocale.value) return;
  storeCurrentLocaleConfig();
  selectedLocale.value = locale;
  const localeData = localeConfigs.value[locale];
  cardConfig.value = localeData?.card
    ? JSON.parse(JSON.stringify(localeData.card))
    : {};
  listConfig.value = localeData?.list
    ? JSON.parse(JSON.stringify(localeData.list))
    : {};
};

const getLocaleLabel = code => {
  if (code === 'default') return 'Default';
  const lang = AVAILABLE_LANGUAGES.find(l => l.code === code);
  return lang?.name || code;
};

// Get default AI prompt for field type
const getDefaultPromptForField = fieldType => {
  const prompts = {
    title: 'Generate an appropriate title',
    description: 'Create a relevant description',
    media_url: 'Generate an appropriate image URL',
    uri: 'Generate appropriate action URL',
    label: 'View Details',
  };
  return prompts[fieldType] || `Generate appropriate ${fieldType}`;
};

// Add a new field to the configuration
const addField = fieldType => {
  const config = editingTemplate.value === 'list' ? listConfig : cardConfig;
  const defaultType = editingTemplate.value === 'list' ? 'property' : 'prompt';
  const promptText = getDefaultPromptForField(fieldType);

  config.value[fieldType] = {
    type: defaultType,
    field: '',
    prompt: defaultType === 'prompt' ? promptText : '',
  };
};

// Remove a field from the configuration
const removeField = fieldKey => {
  const config = editingTemplate.value === 'list' ? listConfig : cardConfig;
  delete config.value[fieldKey];
};

// Get available fields that can be added (not already in config)
const availableFieldsToAdd = computed(() => {
  return availableFieldTypes.value.filter(
    field => !currentConfig.value[field.id]
  );
});

// Helper methods for field labels and icons
const getFieldLabel = fieldKey => {
  const fieldType = availableFieldTypes.value.find(f => f.id === fieldKey);
  return fieldType ? fieldType.label : fieldKey;
};

const getFieldIcon = fieldKey => {
  const fieldType = availableFieldTypes.value.find(f => f.id === fieldKey);
  return fieldType ? fieldType.icon : 'i-lucide-circle';
};

// Set input type (toggle between AI, Var, and Fix) - only for card templates
const setInputType = (field, type) => {
  // List templates only support variables
  if (
    editingTemplate.value === 'list' &&
    (type === 'prompt' || type === 'fixed')
  ) {
    return;
  }

  const config = editingTemplate.value === 'list' ? listConfig : cardConfig;
  const target = config.value[field];
  target.type = type;

  // Clear unused fields based on type
  if (type === 'prompt') {
    target.field = '';
    target.value = '';
  } else if (type === 'property') {
    target.prompt = '';
    target.value = '';
  } else if (type === 'fixed') {
    target.field = '';
    target.prompt = '';
  }
};

// Update field property value
const updateFieldProperty = (fieldKey, property, value) => {
  const config = editingTemplate.value === 'list' ? listConfig : cardConfig;
  if (config.value[fieldKey]) {
    config.value[fieldKey][property] = value;
  }
};

const getDefaultCardConfig = () => ({
  title: {
    type: 'prompt',
    prompt: 'Generate an appropriate title',
  },
  description: {
    type: 'prompt',
    prompt: 'Create a relevant description',
  },
  uri: {
    type: 'property',
    field:
      availableFields.value.length > 0 ? availableFields.value[0].value : '',
  },
  label: {
    type: 'fixed',
    value: 'View Details',
  },
});

const getDefaultListConfig = () => ({
  title: {
    type: 'property',
    field:
      availableFields.value.length > 0 ? availableFields.value[0].value : '',
  },
  description: {
    type: 'property',
    field:
      availableFields.value.length > 1 ? availableFields.value[1].value : '',
  },
  uri: {
    type: 'property',
    field:
      availableFields.value.length > 2 ? availableFields.value[2].value : '',
  },
});

const buildTemplateConfig = (type, configObj) => {
  const templateConfig = { type, config: {} };

  // Helper to build field config
  const buildFieldConfig = (fieldName, fieldConfig) => {
    if (
      fieldConfig &&
      (fieldConfig.field || fieldConfig.prompt || fieldConfig.value)
    ) {
      const result = { type: fieldConfig.type };
      if (fieldConfig.type === 'property') {
        result.field = fieldConfig.field;
      } else if (fieldConfig.type === 'prompt') {
        result.prompt = fieldConfig.prompt;
      } else if (fieldConfig.type === 'fixed') {
        result.value = fieldConfig.value;
      }
      return result;
    }
    return null;
  };

  // Build all field configs
  Object.keys(configObj).forEach(fieldName => {
    const fieldConfig = buildFieldConfig(fieldName, configObj[fieldName]);
    if (fieldConfig) {
      templateConfig.config[fieldName] = fieldConfig;
    }
  });

  // If config is empty, use default configuration
  if (Object.keys(templateConfig.config).length === 0) {
    const defaultConfig =
      type === 'card' ? getDefaultCardConfig() : getDefaultListConfig();
    Object.keys(defaultConfig).forEach(fieldName => {
      const fieldConfig = buildFieldConfig(fieldName, defaultConfig[fieldName]);
      if (fieldConfig) {
        templateConfig.config[fieldName] = fieldConfig;
      }
    });
  }

  return templateConfig;
};

const saveTemplateConfig = async () => {
  isSaving.value = true;
  try {
    // Store current locale's working config
    storeCurrentLocaleConfig();

    // Build the full template config with all locale variants
    const templateConfig = {};

    Object.entries(localeConfigs.value).forEach(([locale, configs]) => {
      if (configs.card && Object.keys(configs.card).length > 0) {
        const key = locale === 'default' ? 'card' : `card_${locale}`;
        templateConfig[key] = buildTemplateConfig('card', configs.card);
      }
      if (configs.list && Object.keys(configs.list).length > 0) {
        const key = locale === 'default' ? 'list' : `list_${locale}`;
        templateConfig[key] = buildTemplateConfig('list', configs.list);
      }
    });

    await store.dispatch('knowledgebase/updateTemplateConfig', {
      knowledgebaseId: props.knowledgebaseId,
      templateConfig,
    });

    editingTemplate.value = null; // Back to overview
    emit('saved');
  } catch (error) {
    // Handle error silently for now
  } finally {
    isSaving.value = false;
  }
};

const handleClose = () => {
  emit('close');
};

const loadTemplateConfig = () => {
  if (!knowledgebase.value?.template_config) return;

  const config = knowledgebase.value.template_config;
  const newLocaleConfigs = {};

  // Parse compound keys like 'card', 'card_fr', 'list', 'list_nl'
  Object.entries(config).forEach(([key, value]) => {
    if (!value?.config) return;
    const match = key.match(/^(card|list)(?:_(.+))?$/);
    if (!match) return;

    const templateType = match[1];
    const locale = match[2] || 'default';

    if (!newLocaleConfigs[locale]) newLocaleConfigs[locale] = {};

    const fieldConfigs = {};
    Object.keys(value.config).forEach(fieldKey => {
      fieldConfigs[fieldKey] = { ...value.config[fieldKey] };
    });
    newLocaleConfigs[locale][templateType] = fieldConfigs;
  });

  localeConfigs.value = newLocaleConfigs;

  // Rebuild activeLocales from what was saved
  const locales = Object.keys(newLocaleConfigs);
  activeLocales.value = ['default', ...locales.filter(l => l !== 'default')];

  // Load working configs for the currently selected locale
  const currentData = localeConfigs.value[selectedLocale.value];
  cardConfig.value = currentData?.card
    ? JSON.parse(JSON.stringify(currentData.card))
    : {};
  listConfig.value = currentData?.list
    ? JSON.parse(JSON.stringify(currentData.list))
    : {};
};

// Watch for knowledgebase changes to load config
watch(knowledgebase, loadTemplateConfig, { immediate: true });

onMounted(() => {
  if (props.knowledgebaseId) {
    store.dispatch('knowledgebase/fetchActiveSchema', props.knowledgebaseId);
    loadTemplateConfig();
  }
});
</script>

<template>
  <div class="h-full bg-white dark:bg-slate-900">
    <!-- Overview: Show both templates (no preview column) -->
    <div v-if="!editingTemplate" class="h-full flex flex-col">
      <!-- Header -->
      <div
        class="p-6 border-b border-slate-75 dark:border-slate-800 flex items-center justify-between"
      >
        <div>
          <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {{ t('LIBRARIES.TEMPLATE_CONFIG.OVERVIEW.TITLE') }}
          </h2>
          <p class="text-sm text-slate-600 dark:text-slate-400 !mb-0">
            {{ t('LIBRARIES.TEMPLATE_CONFIG.OVERVIEW.SUBTITLE') }}
          </p>
        </div>
        <NextButton
          icon="i-lucide-x"
          variant="ghost"
          size="md"
          @click="handleClose"
        />
      </div>

      <!-- Locale Tabs -->
      <div
        class="flex items-center px-6 gap-2 border-b border-slate-200 dark:border-slate-700"
      >
        <Tabs
          :index="activeLocaleTabIndex"
          :border="false"
          @change="handleLocaleTabChanged"
        >
          <li
            v-for="(tab, index) in activeLocaleTabs"
            :key="tab.code"
            class="flex-shrink-0 my-0 mx-2 ltr:first:ml-0 rtl:first:mr-0 ltr:last:mr-0 rtl:last:ml-0 hover:text-n-slate-12"
          >
            <a
              class="flex items-center gap-1 border-b select-none cursor-pointer text-sm relative top-[1px] transition-[border-color] duration-[150ms] ease-[cubic-bezier(0.37,0,0.63,1)] py-3"
              :class="
                index === activeLocaleTabIndex
                  ? 'border-b border-n-brand text-n-blue-text'
                  : 'border-transparent text-n-slate-11'
              "
              @click="handleLocaleTabChanged(index)"
            >
              {{ tab.label }}
              <button
                v-if="tab.code !== 'default'"
                class="flex items-center justify-center w-3.5 h-3.5 rounded-full hover:bg-n-alpha-black2 dark:hover:bg-n-solid-3 text-current opacity-60 hover:opacity-100"
                @click.stop="removeLanguage(tab.code)"
              >
                <span class="i-lucide-x size-2.5" />
              </button>
            </a>
          </li>
        </Tabs>

        <!-- Add Language Button -->
        <div ref="languageDropdownRef" class="relative ml-1">
          <button
            class="flex items-center justify-center w-8 h-8 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:text-slate-300 dark:hover:bg-slate-800 transition-colors"
            @click="showLanguageDropdown = !showLanguageDropdown"
          >
            <Icon icon="i-lucide-plus" class="w-4 h-4" />
          </button>
          <DropdownMenu
            v-if="showLanguageDropdown"
            :menu-items="addableLanguageMenuItems"
            :show-search="true"
            :search-placeholder="t('LIBRARIES.TEMPLATE_CONFIG.SEARCH_LANGUAGE')"
            class="top-full left-0 mt-1 w-56"
            @action="addLanguage"
          />
        </div>
      </div>

      <!-- Template Types -->
      <div class="flex-1 overflow-y-auto p-6">
        <div class="grid grid-cols-2 gap-6 max-w-4xl mx-auto">
          <div
            v-for="templateType in templateTypes"
            :key="templateType.type"
            class="flex flex-col h-full flex flex-col outline outline-n-container outline-1 bg-n-alpha-3 rounded-md shadow"
          >
            <!-- Template Preview (full width at top) -->
            <div
              class="bg-slate-50 dark:bg-slate-800/50 p-6 flex justify-center items-center min-h-[280px]"
            >
              <!-- Card Preview -->
              <TemplateCardPreview
                v-if="templateType.type === 'card'"
                :config="cardConfig"
                :is-default="
                  !cardConfig.title &&
                  !cardConfig.description &&
                  !cardConfig.media_url &&
                  !cardConfig.uri
                "
                class="w-full max-w-sm"
              />

              <!-- List Preview -->
              <TemplateListPreview
                v-if="templateType.type === 'list'"
                :config="listConfig"
                :is-default="
                  !listConfig.title &&
                  !listConfig.description &&
                  !listConfig.uri
                "
                class="w-full max-w-sm"
              />
            </div>

            <!-- Template Info and Controls -->
            <div class="p-6 flex flex-col flex-1">
              <h3 class="font-semibold text-slate-900 dark:text-slate-100 mb-2">
                {{ templateType.name }}
              </h3>

              <p class="text-sm text-slate-600 dark:text-slate-400 mb-4">
                {{ templateType.description }}
              </p>

              <div class="mt-auto">
                <NextButton
                  variant="outline"
                  size="md"
                  @click="startEditingTemplate(templateType.type)"
                >
                  <Icon icon="i-lucide-settings-2" class="w-4 h-4 mr-2" />
                  {{ t('LIBRARIES.TEMPLATE_CONFIG.CUSTOMIZE_TEMPLATE') }}
                </NextButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit View: Configure specific template (with form and preview) -->
    <div v-else class="h-full flex">
      <!-- Left: Configuration Form (2/3 width) -->
      <div
        class="w-2/3 flex flex-col border-r border-slate-75 dark:border-slate-700"
      >
        <!-- Header -->
        <div
          class="p-6 border-b border-slate-75 dark:border-slate-800 flex items-center justify-between"
        >
          <div class="flex items-center space-x-4">
            <NextButton
              icon="i-lucide-arrow-left"
              variant="ghost"
              size="md"
              @click="backToOverview"
            />
            <div>
              <h2
                class="text-lg font-semibold text-slate-900 dark:text-slate-100"
              >
                {{
                  t('LIBRARIES.TEMPLATE_CONFIG.EDIT.TITLE', {
                    type:
                      editingTemplate === 'card'
                        ? t('LIBRARIES.TEMPLATE_CONFIG.EDIT.CARD')
                        : t('LIBRARIES.TEMPLATE_CONFIG.EDIT.LIST'),
                  })
                }}
                <span
                  v-if="selectedLocale !== 'default'"
                  class="ml-2 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400"
                >
                  {{ getLocaleLabel(selectedLocale) }}
                </span>
              </h2>
              <p class="text-sm text-slate-600 dark:text-slate-400 !mb-0">
                {{ t('LIBRARIES.TEMPLATE_CONFIG.EDIT.SUBTITLE') }}
              </p>
            </div>
          </div>
        </div>

        <!-- Form Content -->
        <div class="flex-1 overflow-y-auto p-6 space-y-6">
          <!-- Field Configuration -->
          <div class="space-y-4">
            <TemplateFieldControl
              v-for="(field, fieldKey) in currentConfig"
              :key="fieldKey"
              :field-key="fieldKey"
              :field="field"
              :field-label="getFieldLabel(fieldKey)"
              :field-icon="getFieldIcon(fieldKey)"
              :available-fields="availableFields"
              :template-type="editingTemplate"
              @remove="removeField"
              @update:type="setInputType"
              @update:field="updateFieldProperty"
            />

            <!-- Add Field Button -->
            <div v-if="availableFieldsToAdd.length > 0" class="mt-4">
              <button
                v-for="fieldType in availableFieldsToAdd"
                :key="fieldType.id"
                class="inline-flex items-center space-x-2 px-4 py-2 text-base font-medium text-slate-600 dark:text-slate-400 border border-dashed border-slate-300 dark:border-slate-600 rounded-lg hover:border-slate-400 dark:hover:border-slate-500 hover:text-slate-800 dark:hover:text-slate-200 transition-colors mr-2 mb-2"
                @click="addField(fieldType.id)"
              >
                <Icon :icon="fieldType.icon" class="w-4 h-4" />
                <span>{{
                  t('LIBRARIES.TEMPLATE_CONFIG.FIELDS.ADD', {
                    field: fieldType.label,
                  })
                }}</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="border-t border-slate-75 dark:border-slate-800 p-6">
          <div class="flex justify-end space-x-3">
            <NextButton variant="ghost" @click="backToOverview">
              {{ t('LIBRARIES.TEMPLATE_CONFIG.ACTIONS.CANCEL') }}
            </NextButton>
            <NextButton
              variant="solid"
              :loading="isSaving"
              :disabled="isSaving"
              @click="saveTemplateConfig"
            >
              {{ t('LIBRARIES.TEMPLATE_CONFIG.ACTIONS.SAVE') }}
            </NextButton>
          </div>
        </div>
      </div>

      <!-- Right: Preview Panel (1/3 width) -->
      <div class="w-1/3 flex flex-col bg-slate-50 dark:bg-slate-800/30">
        <div class="p-6 border-b border-slate-75 dark:border-slate-700">
          <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {{ t('LIBRARIES.TEMPLATE_CONFIG.PREVIEW.TITLE') }}
          </h3>
          <p class="text-sm text-slate-600 dark:text-slate-400 !mb-0">
            {{ t('LIBRARIES.TEMPLATE_CONFIG.PREVIEW.SUBTITLE') }}
          </p>
        </div>

        <div class="flex-1 p-6">
          <div class="space-y-4">
            <!-- Card Preview -->
            <TemplateCardPreview
              v-if="editingTemplate === 'card'"
              :config="currentConfig"
            />

            <!-- List Preview -->
            <TemplateListPreview
              v-if="editingTemplate === 'list'"
              :config="currentConfig"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
